/** Strip emoji / most special chars; keep letters, numbers, spaces, basic punctuation. */
export function sanitizeCampaignName(value: string) {
  return value
    .replace(
      /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}]/gu,
      '',
    )
    .replace(/[^\w\s\-_.&()]/g, '')
    .slice(0, 50);
}

export function todayIsoDate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function tomorrowIsoDate() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export function addDaysIso(iso: string, days: number) {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Max window for campaign start/end relative to the earliest allowed start (2 years). */
export const CAMPAIGN_DATE_MAX_DAYS = 730;

export function campaignDateMax(minStart: string) {
  return addDaysIso(minStart, CAMPAIGN_DATE_MAX_DAYS);
}

/**
 * Validates start/end against min start and the 2-year window.
 * Empty required messages are included when dates are missing.
 */
export function validateCampaignDateRange(
  startDate: string,
  endDate: string,
  minStart: string,
  messages?: { requiredStart?: string; requiredEnd?: string },
): Record<string, string> {
  const errors: Record<string, string> = {};
  const maxStart = campaignDateMax(minStart);

  if (!startDate) {
    errors.startDate = messages?.requiredStart || 'Start date is required';
  } else if (startDate < minStart) {
    errors.startDate = `Start date cannot be before ${minStart}`;
  } else if (startDate > maxStart) {
    errors.startDate = 'Start date cannot be more than 2 years ahead';
  }

  if (!endDate) {
    errors.endDate = messages?.requiredEnd || 'End date is required';
  } else if (startDate && endDate < startDate) {
    errors.endDate = 'End date must be on or after the start date';
  } else if (startDate && endDate > campaignDateMax(startDate)) {
    errors.endDate = 'End date cannot be more than 2 years after the start date';
  }

  return errors;
}

export function enumerateDates(start: string, end: string, maxDays = 60) {
  if (!start || !end) return [] as string[];
  const out: string[] = [];
  let cur = start;
  let guard = 0;
  while (cur <= end && guard < maxDays) {
    out.push(cur);
    cur = addDaysIso(cur, 1);
    guard += 1;
  }
  return out;
}

export function formatHourLabel(hour: number) {
  const h = ((Number(hour) % 24) + 24) % 24;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:00 ${period}`;
}

/** e.g. hour 10 → "10:00 AM to 11:00 AM" */
export function formatHourRange(hour: number) {
  const start = Number(hour);
  const end = (start + 1) % 24;
  return `${formatHourLabel(start)} to ${formatHourLabel(end)}`;
}

/** e.g. hour 12 → "12 pm - 1 pm" */
export function formatHourRangeCompact(hour: number) {
  const compact = (value: number) => {
    const h = ((Number(value) % 24) + 24) % 24;
    const period = h >= 12 ? 'pm' : 'am';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12} ${period}`;
  };
  const start = Number(hour);
  const end = (start + 1) % 24;
  return `${compact(start)} - ${compact(end)}`;
}

/**
 * API accepts either galleryId or mediaIds — never both.
 * Prefer galleryId when present.
 */
export function creativeSourcePayload(input: {
  galleryId?: string | null;
  mediaIds?: Array<{
    mediaId: string;
    orientation?: string;
    mediaType?: string;
  }> | null;
}) {
  const galleryId = input.galleryId?.trim();
  if (galleryId) {
    return { galleryId };
  }
  const mediaIds = (input.mediaIds || [])
    .filter((m) => m?.mediaId)
    .map(({ mediaId, orientation, mediaType }) => ({
      mediaId,
      ...(orientation ? { orientation } : {}),
      ...(mediaType ? { mediaType } : {}),
    }));
  return mediaIds.length ? { mediaIds } : {};
}
